import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Debounce function to limit how often a function can be called
 * Useful for search inputs, filter changes, etc.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Decodes HTML entities like &lt; -> <, &amp; -> &, etc.
 * Handles double-encoded entities (e.g., &amp;lt; -> <)
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text;
  
  // First, manually decode common escaped HTML entities that appear as text
  // This handles cases where the API returns already-escaped HTML like "&lt;p&gt;"
  let decoded = text
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
  
  // Handle double-encoded entities (e.g., &amp;lt; -> &lt; -> <)
  if (decoded.includes('&lt;') || decoded.includes('&gt;') || decoded.includes('&amp;')) {
    decoded = decoded
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&');
  }
  
  return decoded;
}

/**
 * Strips markdown formatting, HTML tags, and decodes HTML entities from text.
 * Useful for cleaning job descriptions that contain raw HTML or markdown.
 */
export function stripHtmlAndDecode(text: string): string {
  if (!text) return text;
  
  // First decode HTML entities (e.g., &lt; -> <, &amp; -> &)
  let decoded = decodeHtmlEntities(text);
  
  // Convert markdown links [text](url) to just "text"
  decoded = decoded.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // Remove remaining markdown URLs (bare URLs in markdown)
  decoded = decoded.replace(/\]\([^)]+\)/g, '');
  
  // Remove markdown bold/italic markers
  decoded = decoded.replace(/(\*\*|__)(.*?)\1/g, '$2'); // Bold
  decoded = decoded.replace(/(\*|_)(.*?)\1/g, '$2'); // Italic
  
  // Remove markdown headers
  decoded = decoded.replace(/^#{1,6}\s+/gm, '');
  
  // Remove markdown bullet points (but keep the text)
  decoded = decoded.replace(/^[\s]*[-*+]\s+/gm, '• ');
  
  // Remove markdown numbered lists (but keep the text)
  decoded = decoded.replace(/^[\s]*\d+\.\s+/gm, '');
  
  // Replace common block elements with newlines for better formatting
  decoded = decoded
    .replace(/<\/?(p|div|br|h[1-6]|li|tr|section|article|header|footer)[^>]*>/gi, '\n')
    .replace(/<\/?(ul|ol|table|tbody|thead)[^>]*>/gi, '\n');
  
  // Remove all remaining HTML tags
  const stripped = decoded.replace(/<[^>]*>/g, ' ');
  
  // Clean up extra whitespace while preserving paragraph breaks
  return stripped
    .replace(/[ \t]+/g, ' ')           // Multiple spaces/tabs -> single space
    .replace(/\n[ \t]+/g, '\n')        // Trim start of lines
    .replace(/[ \t]+\n/g, '\n')        // Trim end of lines
    .replace(/\n{3,}/g, '\n\n')        // Max 2 consecutive newlines
    .trim();
}

/**
 * Check if a company name looks like a domain or job platform URL.
 * Returns true if the name should be hidden/replaced.
 */
export function isCompanyNameDomainLike(company: string): boolean {
  if (!company) return true;
  const c = company.toLowerCase().trim();
  
  // Empty or unknown
  if (!c || c === "unknown" || c === "okänt") return true;
  
  // Common job platform patterns
  const jobPlatformPatterns = [
    /^jobs\./i,
    /^careers\./i,
    /^career\./i,
    /^apply\./i,
    /^boards\./i,
    /careers$/i,
    /jobs$/i,
  ];
  
  for (const pattern of jobPlatformPatterns) {
    if (pattern.test(c)) return true;
  }
  
  // Looks like a domain (contains dots and TLD patterns)
  if (/^[a-z0-9.-]+\.(com|io|co|se|net|org|ai|app|dev|jobs|careers|work)$/i.test(c)) {
    return true;
  }
  
  // Known job board/platform domains that shouldn't be shown as company names
  const blockedDomains = [
    "khoslaventures", "capitalonecareers", "roberthalf", "johnsoncontrols",
    "weworkremotely", "remoteok", "flexjobs", "himalayas", "justremote",
    "remotive", "indeed", "linkedin", "glassdoor", "monster", "stepstone",
    "dice", "ziprecruiter", "careerbuilder", "simplyhired", "lever",
    "greenhouse", "teamtailor", "ashbyhq", "smartrecruiters", "workable",
    "bamboohr", "recruitee", "breezy", "jazzhr", "personio", "factorial",
    "workday", "myworkdayjobs", "icims", "taleo", "successfactors",
  ];
  
  // Check if company name matches any blocked domain (with or without TLD)
  const companyBase = c.replace(/\.(com|io|co|se|net|org|ai|app|dev|jobs|careers|work)$/i, "");
  if (blockedDomains.some(domain => companyBase === domain || c.includes(domain))) {
    return true;
  }
  
  return false;
}

import { checkTrustedDomain, type DomainCheckResult } from "./trustedDomains";

export interface SafeUrlResult {
  url: string;
  isTrusted: boolean;
  displayDomain: string;
}

/**
 * Normalize URL input: domain.com → https://domain.com (P0 URL UX).
 * Use before validation so "sannadahlin.com" is accepted and stored as https://sannadahlin.com.
 */
function normalizeUrlInput(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  // No scheme: add https:// if it looks like a domain (has dot, no spaces)
  if (/^[^\s]+\.[^\s]+$/.test(trimmed) || trimmed.toLowerCase().startsWith("www.")) {
    return trimmed.toLowerCase().startsWith("www.") ? `https://${trimmed}` : `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Validate and normalize external URLs to avoid unsafe protocols.
 * P0: domain.com is normalized to https://domain.com; error only for truly invalid (spaces, no domain, etc).
 */
export function getSafeExternalUrl(url: string | null | undefined): string | null {
  if (url == null || typeof url !== "string") return null;
  if (typeof window === "undefined") return null;

  try {
    const trimmed = url.trim();
    if (!trimmed) return null;

    const normalized = normalizeUrlInput(trimmed);
    if (normalized !== trimmed && !normalized.startsWith("http")) return null;

    const parsed = new URL(normalized);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Get safe URL with trust information for UI display.
 * Used to show domain verification status before opening external links.
 */
export function getSafeUrlWithTrust(url: string | null | undefined): SafeUrlResult | null {
  const safeUrl = getSafeExternalUrl(url);
  if (!safeUrl) return null;
  
  const domainCheck = checkTrustedDomain(safeUrl);
  return {
    url: safeUrl,
    isTrusted: domainCheck?.isTrusted ?? false,
    displayDomain: domainCheck?.displayDomain ?? new URL(safeUrl).hostname,
  };
}

/**
 * Open external URL in a new tab if it is safe.
 * Returns trust information that can be used to show warnings for untrusted domains.
 */
export function openExternalUrl(url: string | null | undefined): boolean {
  const safeUrl = getSafeExternalUrl(url);
  if (!safeUrl) return false;
  if (typeof window === "undefined") return false;
  window.open(safeUrl, "_blank", "noopener,noreferrer");
  return true;
}

/**
 * Re-export for convenience
 */
export { checkTrustedDomain, isTrustedDomain, getDisplayDomain } from "./trustedDomains";
export type { DomainCheckResult } from "./trustedDomains";
